import type { MidiDevice, MidiCC } from '~/types/midi';
import type { DevicePattern } from '~/types';
import { getDefaultDevicePattern } from '~/lib/patterns/defaultPattern';
import { getCCPresetManager } from '~/lib/presets/CCPresetManager';
import { getPatternPresetManager } from '~/lib/presets/PatternPresetManager';
import { applyProfileToCCs } from '~/lib/profiles/applyProfile';
import packageJson from '../../../package.json';
import initProjectJson from '~/config/projects/init.json';

/**
 * Current storage version from package.json
 * This ensures version is maintained in a single place
 */
export const CURRENT_STORAGE_VERSION = packageJson.version;

const STORAGE_KEYS = {
  DEVICES: 'yomidi-devices',
  VERSION: 'yomidi-storage-version',
} as const;

/**
 * Hydrate device from preset IDs
 *
 * This function enables "slim" devices that only store preset IDs instead of
 * full CC arrays and pattern objects. It follows these rules:
 *
 * 1. If ccPresetId exists AND cc is undefined → Load CCs from preset
 * 2. If ccPresetId exists AND cc is defined → Keep cc (user modified preset)
 * 3. If ccPresetId is null → Keep cc as-is (custom CCs)
 * 4. Same logic applies for patternPresetId and pattern
 *
 * This allows:
 * - Slim project files (only IDs, no data)
 * - User modifications to presets (ID + data = modified)
 * - Custom values not based on presets (null ID + data = custom)
 *
 * @param device - Device with potential preset IDs
 * @returns Device with hydrated CC/pattern data
 */
export function hydrateDeviceFromPresets(device: Partial<MidiDevice>): Partial<MidiDevice> {
  const ccPresetManager = getCCPresetManager();
  const patternPresetManager = getPatternPresetManager();

  // Clone device to avoid mutations
  const hydrated = { ...device };

  // Hydrate CC from preset if needed
  if (device.ccPresetId && device.cc === undefined) {
    const ccPreset = ccPresetManager.getPreset(device.ccPresetId);
    if (ccPreset) {
      // Deep copy CC array to avoid preset mutations
      hydrated.cc = ccPreset.cc.map(cc => ({ ...cc }));

      // Also set profiles and activeProfileId if not already set
      if (!hydrated.profiles) {
        hydrated.profiles = ccPreset.profiles.map(profile => ({
          ...profile,
          ccConfig: profile.ccConfig.map(config => ({ ...config })),
        }));
      }
      if (!hydrated.activeProfileId) {
        hydrated.activeProfileId = ccPreset.activeProfileId;
      }
    }
  }

  // Hydrate pattern from preset if needed
  if (device.patternPresetId && device.pattern === undefined) {
    const patternPreset = patternPresetManager.getPattern(device.patternPresetId);
    if (patternPreset) {
      // Deep copy pattern object to avoid preset mutations
      hydrated.pattern = {
        ...patternPreset.pattern,
        pattern: { ...patternPreset.pattern.pattern },
        patternParts: Object.fromEntries(
          Object.entries(patternPreset.pattern.patternParts).map(([key, value]) => [
            key,
            value.map(note => ({ ...note })),
          ])
        ) as any,
        scrambleToggles: { ...patternPreset.pattern.scrambleToggles },
      };
    }
  }

  return hydrated;
}

/**
 * Get the storage version from localStorage
 * @returns Version string or null if not set
 */
export function getStorageVersion(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEYS.VERSION);
  } catch {
    return null;
  }
}

/**
 * Set the storage version in localStorage
 */
function setStorageVersion(version: string): void {
  try {
    localStorage.setItem(STORAGE_KEYS.VERSION, version);
  } catch (err) {
    console.warn('Failed to set storage version:', err);
  }
}

/**
 * Validate and sanitize a MIDI CC value
 * Preserves null values to represent unknown/uninitialized state
 */
function sanitizeCC(cc: any): MidiCC | null {
  if (!cc || typeof cc !== 'object') return null;

  // Support both old format (control) and new format (number)
  const control = Number(cc.number ?? cc.control);

  // Validate control is a valid MIDI number (0-127)
  if (isNaN(control) || control < 0 || control > 127) return null;

  // Handle value: null = unknown state, otherwise must be 0-127
  let value: number | null = null;
  if (cc.value !== null && cc.value !== undefined) {
    const numValue = Number(cc.value);
    if (isNaN(numValue) || numValue < 0 || numValue > 127) return null;
    value = numValue;
  }

  // Ensure label exists (migration for old data)
  const label = typeof cc.label === 'string' && cc.label ? cc.label : `CC ${control}`;

  return {
    number: control,
    value, // Preserves null for unknown state
    scramble: Boolean(cc.scramble ?? false),
    smooth: Boolean(cc.smooth ?? false),
    show: Boolean(cc.show ?? true),
    min: typeof cc.min === 'number' ? cc.min : 0,
    max: typeof cc.max === 'number' ? cc.max : 127,
    label,
  };
}

/**
 * Validate and sanitize a device pattern
 */
function sanitizePattern(pattern: any): DevicePattern | null {
  if (!pattern || typeof pattern !== 'object') return null;

  const { pattern: patternInfo, patternParts } = pattern;

  // Validate pattern structure
  if (!patternInfo || typeof patternInfo !== 'object') return null;
  if (!Array.isArray(patternInfo.sequence)) return null;

  // Validate sequence contains only valid part names (A-E)
  const validPartNames = ['A', 'B', 'C', 'D', 'E'];
  const isValidSequence = patternInfo.sequence.every((part: any) =>
    typeof part === 'string' && validPartNames.includes(part)
  );

  if (!isValidSequence) return null;

  // Validate patternParts
  if (!patternParts || typeof patternParts !== 'object') return null;

  // CRITICAL: Must have at least parts A and B (required for all patterns)
  if (!Array.isArray(patternParts.A) || !Array.isArray(patternParts.B)) return null;

  // CRITICAL: Parts A and B must not be empty (need actual notes for playback)
  if (patternParts.A.length === 0 || patternParts.B.length === 0) return null;

  // CRITICAL: Validate each note has required fields (note, duration)
  const validateNotes = (notes: any[]) => {
    return notes.every(note =>
      note &&
      typeof note === 'object' &&
      'note' in note &&
      'duration' in note &&
      typeof note.duration === 'string'
    );
  };

  if (!validateNotes(patternParts.A) || !validateNotes(patternParts.B)) return null;

  // Validate optional parts (C, D, E) if present
  for (const key of ['C', 'D', 'E']) {
    if (key in patternParts) {
      if (!Array.isArray(patternParts[key])) return null;
      if (patternParts[key].length > 0 && !validateNotes(patternParts[key])) return null;
    }
  }

  // Add min/max fields if missing (for backward compatibility with old localStorage data)
  // Also add tempo and swing with defaults if missing
  return {
    ...pattern,
    tempo: pattern.tempo ?? 110, // Default BPM
    swing: pattern.swing ?? 0, // Default swing amount (0 = no swing)
    spaceMin: pattern.spaceMin ?? 0,
    spaceMax: pattern.spaceMax ?? 100,
    dissonanceMin: pattern.dissonanceMin ?? 0,
    dissonanceMax: pattern.dissonanceMax ?? 100,
    spreadMin: pattern.spreadMin ?? 0,
    spreadMax: pattern.spreadMax ?? 88,
    rangeMin: pattern.rangeMin ?? 1,
    rangeMax: pattern.rangeMax ?? 88,
  } as DevicePattern;
}

/**
 * Get default device pattern with actual notes
 * Uses the centralized default pattern generator
 */
function getDefaultPattern(): DevicePattern {
  return getDefaultDevicePattern();
}

/**
 * Sanitize and validate a device object
 *
 * Supports two device formats:
 * 1. Slim devices: Only have preset IDs (ccPresetId, patternPresetId), no data arrays
 * 2. Full devices: Have complete cc, profiles, activeProfileId, and pattern data
 */
function sanitizeDevice(device: any): Partial<MidiDevice> | null {
  if (!device || typeof device !== 'object') return null;

  // Required field: label
  if (typeof device.label !== 'string' || !device.label) return null;

  // Check if this is a slim device (has preset IDs but may not have full data)
  const hasPresetIds = device.ccPresetId || device.patternPresetId;
  const hasCCData = Array.isArray(device.cc) && Array.isArray(device.profiles) && typeof device.activeProfileId === 'string';

  // Slim devices: Only validate preset IDs exist, skip CC/profile validation
  if (hasPresetIds && !hasCCData) {
    const slimDevice: Partial<MidiDevice> = {
      label: device.label,
      uniqueId: typeof device.uniqueId === 'string' ? device.uniqueId : undefined,
      productId: typeof device.productId === 'string' ? device.productId : undefined,
      enabled: Boolean(device.enabled ?? true),
      active: Boolean(device.active ?? true),
      panelOpen: typeof device.panelOpen === 'boolean' ? device.panelOpen : undefined,
      synthType: device.synthType === 'MonoSynth' || device.synthType === 'FMSynth' ? device.synthType : undefined,
      // Preset IDs only - will be hydrated later
      ccPresetId: typeof device.ccPresetId === 'string' ? device.ccPresetId : undefined,
      patternPresetId: typeof device.patternPresetId === 'string' ? device.patternPresetId : undefined,
      // Leave cc, profiles, activeProfileId, and pattern undefined - hydration will fill these
    };
    return slimDevice;
  }

  // Full devices: Require cc, profiles, and activeProfileId
  if (!hasCCData) return null; // Not slim and missing required data = invalid

  // Sanitize CC values
  let sanitizedCCs = device.cc
    .map((cc: any) => sanitizeCC(cc))
    .filter((cc: MidiCC | null): cc is MidiCC => cc !== null);

  // Re-apply active profile to ensure scramble values match profile's enabled values
  // This fixes issues where localStorage has outdated CC scramble values
  if (device.profiles && device.activeProfileId) {
    const activeProfile = device.profiles.find((p: any) => p.id === device.activeProfileId);
    if (activeProfile) {
      sanitizedCCs = applyProfileToCCs(sanitizedCCs, activeProfile);
    }
  }

  // Sanitize pattern or use default
  let pattern: DevicePattern | undefined;
  if (device.pattern) {
    const sanitized = sanitizePattern(device.pattern);
    pattern = sanitized || getDefaultPattern();
  } else {
    pattern = getDefaultPattern();
  }

  // Build sanitized device with defaults
  const sanitized: Partial<MidiDevice> = {
    label: device.label,
    uniqueId: typeof device.uniqueId === 'string' ? device.uniqueId : undefined,
    productId: typeof device.productId === 'string' ? device.productId : undefined,
    cc: sanitizedCCs,
    profiles: device.profiles, // TODO: Add profile validation if needed
    activeProfileId: device.activeProfileId,
    enabled: Boolean(device.enabled ?? true), // Default to true
    active: Boolean(device.active ?? true), // Default to true
    pattern,
    panelOpen: typeof device.panelOpen === 'boolean' ? device.panelOpen : undefined,
    synthType: device.synthType === 'MonoSynth' || device.synthType === 'FMSynth' ? device.synthType : undefined,
    // Preserve preset IDs if they exist
    ccPresetId: typeof device.ccPresetId === 'string' ? device.ccPresetId : undefined,
    patternPresetId: typeof device.patternPresetId === 'string' ? device.patternPresetId : undefined,
  };

  return sanitized;
}

/**
 * Load devices from localStorage with sanitization and hydration
 *
 * This function handles two important steps:
 * 1. Sanitization: Validates and cleans device data from localStorage
 * 2. Hydration: Loads preset data for devices that only have preset IDs
 *
 * @returns Array of devices or null if no valid data exists
 */
export function loadDevicesFromStorage(): Partial<MidiDevice>[] | null {
  try {
    console.log('[STORAGE] Loading devices from localStorage...');
    const stored = localStorage.getItem(STORAGE_KEYS.DEVICES);

    if (!stored) {
      console.log('[STORAGE] No devices found in localStorage');
      return null;
    }

    console.log('[STORAGE] Found stored devices, parsing...');
    const parsed = JSON.parse(stored);

    // Must be an array
    if (!Array.isArray(parsed)) {
      console.warn('[STORAGE] Stored devices is not an array, ignoring');
      return null;
    }

    console.log(`[STORAGE] Parsed ${parsed.length} devices from storage`);

    // Sanitize each device, filtering out invalid ones
    const sanitized = parsed
      .map((device: any) => sanitizeDevice(device))
      .filter((device: Partial<MidiDevice> | null): device is Partial<MidiDevice> => device !== null);

    console.log(`[STORAGE] Sanitized to ${sanitized.length} valid devices`);

    // Hydrate devices from preset IDs (loads slim project files)
    const hydrated = sanitized.map(device => hydrateDeviceFromPresets(device));
    console.log(`[STORAGE] Hydrated ${hydrated.length} devices from presets`);

    return hydrated;
  } catch (err) {
    console.error('[STORAGE] Failed to load devices from storage:', err);
    return null;
  }
}

/**
 * Save devices to localStorage
 * @param devices Array of devices to save
 */
export function saveDevicesToStorage(devices: MidiDevice[]): void {
  try {
    console.log(`[STORAGE] Saving ${devices.length} devices to localStorage...`);

    // Save only the necessary fields to reduce storage size
    const toSave = devices.map(d => ({
      label: d.label,
      uniqueId: d.uniqueId,
      productId: d.productId,
      cc: d.cc,
      profiles: d.profiles,
      activeProfileId: d.activeProfileId,
      enabled: d.enabled,
      active: d.active,
      pattern: d.pattern,
      panelOpen: d.panelOpen,
      synthType: d.synthType,
    }));

    console.log('[STORAGE] Serializing devices to JSON...');
    const serialized = JSON.stringify(toSave);
    console.log(`[STORAGE] Serialized size: ${serialized.length} characters`);

    console.log('[STORAGE] Writing to localStorage key:', STORAGE_KEYS.DEVICES);
    localStorage.setItem(STORAGE_KEYS.DEVICES, serialized);
    console.log('[STORAGE] Successfully saved devices to localStorage');

    // Update version
    console.log('[STORAGE] Setting storage version:', CURRENT_STORAGE_VERSION);
    setStorageVersion(CURRENT_STORAGE_VERSION);
    console.log('[STORAGE] Storage version saved');
  } catch (err) {
    console.warn('Failed to save devices to storage:', err);
  }
}

/**
 * Migration framework for future use
 * Currently just logs version mismatch
 *
 * @param fromVersion The version stored in localStorage
 * @param toVersion The current app version
 * @returns Migrated data (currently just returns the data as-is)
 */
export function migrateStorageData(
  fromVersion: string,
  toVersion: string,
  data: any
): any {
  if (fromVersion === toVersion) return data;

  console.info(`Storage migration: ${fromVersion} → ${toVersion}`);

  // Future migrations can be added here:
  // if (fromVersion === '0.1.0' && toVersion === '0.2.0') {
  //   return migrate_0_1_0_to_0_2_0(data);
  // }

  // For now, just return the data and let sanitization handle any issues
  return data;
}

/**
 * Load init project devices from init.json
 *
 * This function:
 * 1. Loads the slim init.json file (only preset IDs, no data)
 * 2. Hydrates devices with preset data
 * 3. Returns fully hydrated devices ready to use
 *
 * Used for:
 * - First launch (no localStorage)
 * - After reset (/reset route)
 *
 * @returns Array of hydrated devices from init.json
 */
export function loadInitProjectDevices(): Partial<MidiDevice>[] {
  console.log('[INIT] Loading devices from init.json...');

  // Get devices from init.json (slim format with only preset IDs)
  const devices = (initProjectJson as any).devices;

  if (!Array.isArray(devices)) {
    console.error('[INIT] init.json devices is not an array');
    return [];
  }

  console.log(`[INIT] Found ${devices.length} devices in init.json`);

  // Sanitize and hydrate each device
  const sanitized = devices
    .map((device: any) => sanitizeDevice(device))
    .filter((device: Partial<MidiDevice> | null): device is Partial<MidiDevice> => device !== null);

  const hydrated = sanitized.map(device => hydrateDeviceFromPresets(device));

  console.log(`[INIT] Hydrated ${hydrated.length} devices from init.json`);

  return hydrated;
}
